import clsx from "clsx";
import dateFormat from "dateformat";
import isEmpty from "is-empty";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import {
  doFastCheckinMutation,
  expireSessionMutation,
  loginETOModeMutation,
  redirectPaymentGatewayForCartMutation,
  resetCartMutation,
  unlockBookingMutation,
  updateCustomerAccountMutation,
  updateCustomerBookingMutation,
} from "rsl-api-106/lib/mutations";
import {
  getBookingRecordCriteriasQuery,
  getCartQuery,
  getCustomerBookingsQuery,
  hasProductsForOccupantQuery,
} from "rsl-api-106/lib/queries";
import {
  BookingStatus,
  Cart,
  Maybe,
  Mutation,
  MutationDoFastCheckinArgs,
  MutationExpireSessionArgs,
  MutationLoginEtoModeArgs,
  MutationRedirectPaymentGatewayForCartArgs,
  MutationResetCartArgs,
  MutationUnlockBookingArgs,
  MutationUpdateBookingCriteriasArgs,
  MutationUpdateCustomerAccountArgs,
  MutationUpdateCustomerBookingArgs,
  Option,
  Product,
  Query,
  QueryGetBookingRecordCriteriasArgs,
  QueryGetCartArgs,
  QueryGetCustomerBookingsArgs,
  QueryHasProductsForOccupantArgs,
} from "rsl-api-106/lib/types";
import { PspCallbackStatus, SelectOptionType } from "types";

import { ApolloError, useApolloClient, useQuery } from "@apollo/client";
import Button from "@components/_Layout/Controls/Button";
import InputCheckbox from "@components/_Layout/Controls/InputCheckbox";
import InputText from "@components/_Layout/Controls/InputText";
import Popup from "@components/_Layout/Controls/Popup";
import Select from "@components/_Layout/Controls/Select";
import SelectPaymentMethods from "@components/_Layout/Controls/SelectPaymentMethods";
import EmptyState from "@components/_Layout/EmptyState";
import Encart from "@components/_Layout/Encart";
import LoadingDots from "@components/_Layout/Loaders/LoadingDots";
import LoadingScreen from "@components/_Layout/Loaders/LoadingScreen";
import ReservationOccupants from "@components/Occupants/ReservationOccupants";
import { NbOccupantByReservationTitle } from "@components/Reservation/BasketSummary/BasketSummary";
import { StepTitle } from "@components/Reservation/Steps";
import { useAuth, useCart, useSession } from "@hooks";
import {
  ArrowCircleLeft,
  ArrowCircleRight,
  InfoRounded,
  PhoneOutlined,
  PinDropOutlined,
} from "@mui/icons-material";
import {
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
} from "@mui/material";
import {
  doFastCheckinLightMutation,
  getCustomerBookingsIdsQuery,
  hasProductsForOccupantWithoutTaxesQuery,
  QueryHasProductsForOccupantWithoutTaxesArgs,
  updateBookingCriteriasMutation,
} from "@services/graphql2/querymutations";
import { sortBy } from "@utils/arrays";
import { getCurrencySymbol } from "@utils/currencies";
import { focusElementById, getAbsoluteImagePath, scrollTop } from "@utils/dom";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from "@utils/localStorage";
import {
  getAllLineItems,
  getBaseLineItem,
  getFormuleSejour,
  getTotalPaid,
  getTotalRemaining,
  getTotalRemainingIncludingPrereserved,
  getTotalTax,
} from "@utils/tools";

import { COMMISSIONS_PRODUCTS, TAXES_PRODUCTS } from "../constants";
import { StepPrestation } from "./ReservationPage";

enum StepEnum {
  RAPPEL = 0,
  COORDONNEES = 1,
  OPTIONS = 2,
  PAYMENT = 3,
}

enum StepPaymentEnum {
  CAUTION = 0,
  PAYMENT = 1,
  FAIL = 2,
  SUCCESS = 3,
}

enum StepOptionEnum {
  GLOBAL = 0,
  OCCUPANT = 1,
}

type FCIPageProps = {};
const FCIPage: React.FC<FCIPageProps> = ({}) => {
  /* Hooks */
  const { user } = useAuth();
  const { cart, loading } = useCart();
  const [searchParams] = useSearchParams();

  /* Effects */
  useEffect(() => {
    if (!loading) {
      if (searchParams.get("s")) {
        //Si on a un paramètre 's' dans l'URL (retour psp), on ne check pas le panier pour rediriger
      } else {
        if (
          cart &&
          !isEmpty(cart?.reservations) &&
          cart?.fromFastCheckinCheckout === false
        ) {
          window.location.href = window.origin;
        }
      }
    }
  }, [loading]);

  /* Renders */
  if (loading) return <LoadingScreen />;

  {
    /* On a pas de user, on affiche l'intro */
  }
  if (!user || (!loading && (!cart || isEmpty(cart?.reservations))))
    return <FCIIntro />;

  {
    /* On a un panier/resa et un user */
  }
  return <FCIStep />;
};

const FCIIntro = () => {
  /* Hooks */
  const { sessionId, getLanguageVarValue, lang, funnel_type } = useSession();
  const client = useApolloClient();
  const { setUser } = useAuth();
  const { cart, refresh, setCart, setCustomerBooking } = useCart();

  /* States */
  const [bookingRef, setBookingRef] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  /* Functions */
  const convertirFormatDate = (dateString: string): string | null => {
    try {
      // Vérifier que la chaîne ne contient que des chiffres
      if (!/^\d+$/.test(dateString)) {
        return null;
      }

      // Vérifier que la chaîne a la bonne longueur
      if (dateString.length !== 6) {
        return null;
      }

      // Extraire les composants de la date (jour, mois, année)
      const jour = dateString.substr(0, 2);
      const mois = dateString.substr(2, 2);
      const annee = dateString.substr(4, 2);

      // Construire un objet Date avec les composants extraits
      const dateObj = new Date(`20${annee}-${mois}-${jour}`);

      // Vérifier si la date est valide
      if (isNaN(dateObj.getTime())) {
        return null;
      }

      // Formater la date dans le nouveau format "YYYY-MM-DD"
      const nouvelleDate = dateObj.toISOString().slice(0, 10);

      return nouvelleDate;
    } catch (error) {
      return null;
    }
  };

  const handleLoginETOMode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Chaîne de caractères fournie
      const inputString: string = bookingRef;

      // Trouver la position du premier tiret
      const firstDashIndex: number = inputString.indexOf("-");

      // Trouver la position du dernier tiret
      const lastDashIndex: number = inputString.lastIndexOf("-");

      // Extraire l'ID de la réservation jusqu'au premier tiret
      const reservationId: string = inputString.slice(0, firstDashIndex);

      // Extraire la dernière partie à partir du dernier tiret
      const lastNameInitials: string = inputString.slice(lastDashIndex + 1);

      // Extraire la deuxième partie entre les deux tirets
      const middlePart: string = inputString.slice(
        firstDashIndex + 1,
        lastDashIndex
      );

      let convertedDate = convertirFormatDate(middlePart);
      if (convertedDate === null) {
        toast.error(
          getLanguageVarValue("LANG_neobooking_fci_login_format_error")
        );
        setLoading(false);
        return;
      }

      const { data, errors } = await client.mutate<
        Mutation,
        MutationLoginEtoModeArgs
      >({
        mutation: loginETOModeMutation(),
        variables: {
          session: { name: sessionId },
          input: {
            bookingId: reservationId,
            startDate: convertedDate,
            value: lastNameInitials,
          },
        },
        fetchPolicy: "no-cache",
      });
      if (errors) {
        toast.error(errors?.[0]?.message);
      }
      if (data && data.loginETOMode && data.loginETOMode.service) {
        //On maj le 1er occupant:
        const mails = data.loginETOMode.service.address?.mail?.split(";");

        let new_mail = email;
        if (mails && mails?.includes(email)) {
          //on a deja l'email renseigné dans la liste des email, on ne fait rien car deja présente
        } else {
          //on a pas le mail, on le concaténe
          new_mail = `${data.loginETOMode.service.address?.mail};${email}`;
        }

        //on update le mail de l'utilisateur que si celui ci est differents
        if (new_mail !== data.loginETOMode.service.address?.mail) {
          const { data: dataUpdateCustomerAccount } = await client.mutate<
            Mutation,
            MutationUpdateCustomerAccountArgs
          >({
            mutation: updateCustomerAccountMutation(),
            variables: {
              session: { name: sessionId },
              input: {
                firstName: data.loginETOMode.service.firstName!!,
                lastName: data.loginETOMode.service.lastName!!,
                birthDate: data.loginETOMode.service.birthDate,
                address: {
                  mail: new_mail,
                },
              },
            },
            fetchPolicy: "no-cache",
          });

          //Si l'update customeraccount ok on set le user avec ca, sinon avec le loginETOMode
          if (
            dataUpdateCustomerAccount &&
            dataUpdateCustomerAccount.updateCustomerAccount &&
            dataUpdateCustomerAccount.updateCustomerAccount.service
          ) {
            setUser(dataUpdateCustomerAccount.updateCustomerAccount.service);
          } else {
            setUser(data.loginETOMode.service);
          }
        } else {
          setUser(data.loginETOMode.service);
        }

        //On reset le cart
        const { data: dataReset, errors: errorsReset } = await client.mutate<
          Mutation,
          MutationResetCartArgs
        >({
          mutation: resetCartMutation(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        });

        if (errorsReset && errorsReset.length > 0) {
          setLoading(false);
        } else if (dataReset && dataReset.resetCart) {
          //on fait un getcustomerbooking
          const { data: dataBooking } = await client.query<
            Query,
            QueryGetCustomerBookingsArgs
          >({
            query: getCustomerBookingsQuery(),
            variables: {
              session: { name: sessionId },
              input: {},
            },
            fetchPolicy: "no-cache",
          });

          let resaId = reservationId;
          if (
            dataBooking &&
            dataBooking.getCustomerBookings &&
            dataBooking.getCustomerBookings.customerBookings &&
            dataBooking.getCustomerBookings.customerBookings.length > 0
          ) {
            //on a une resa, on fait la suite dessus:
            /*resaId =
              dataBooking?.getCustomerBookings?.customerBookings
                ?.filter(
                  (x) =>
                    x?.id?.toString() === reservationId ||
                    x?.externalCode === reservationId
                )?.[0]
                ?.id?.toString() ?? reservationId;*/

            resaId =
              dataBooking.getCustomerBookings.customerBookings?.[0]?.id?.toString() ??
              reservationId;
          }

          //Le cart est bien reset, on unlock la resa
          const { data: dataUnlock, errors: errorsUnlock } =
            await client.mutate<Mutation, MutationUnlockBookingArgs>({
              mutation: unlockBookingMutation(),
              variables: {
                session: { name: sessionId },
                input: {
                  bookingId: parseInt(resaId),
                },
              },
              fetchPolicy: "no-cache",
            });

          if (errorsUnlock && errorsUnlock.length > 0) {
            setLoading(false);
          } else if (
            dataUnlock &&
            dataUnlock.unlockBooking &&
            dataUnlock.unlockBooking.bookingUnlocked
          ) {
            //On a unlock la resa, on peux doFastcheckin
            const { data: dataFCI, errors: errorsFCI } = await client.mutate<
              Mutation,
              MutationDoFastCheckinArgs
            >({
              mutation: doFastCheckinLightMutation(),
              variables: {
                session: { name: sessionId },
                input: {
                  bookingId: parseInt(resaId),
                },
              },
              fetchPolicy: "no-cache",
            });

            if (errorsFCI && errorsFCI.length > 0) {
              setLoading(false);
            } else if (
              dataFCI &&
              dataFCI.doFastCheckin &&
              dataFCI.doFastCheckin.reservation
            ) {
              //On a dofastcheckin, on get le panier
              const { data: dataGetCart } = await client.query<
                Query,
                QueryGetCartArgs
              >({
                query: getCartQuery(),
                variables: {
                  session: { name: sessionId },
                },
                fetchPolicy: "no-cache",
              });

              if (
                dataGetCart &&
                dataGetCart.getCart &&
                dataGetCart.getCart.cart
              ) {
                setCart(dataGetCart.getCart.cart);
                setCustomerBooking(
                  dataBooking?.getCustomerBookings?.customerBookings?.[0] ??
                    null
                );
              }

              setLoading(false);
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        toast.error(error.message);
      }
      setLoading(false);
    }
  };

  const steps = [
    getLanguageVarValue("LANG_neobooking_step_fci_auth"),
    getLanguageVarValue("LANG_neobooking_step_fci_info"),
    getLanguageVarValue("LANG_neobooking_step_fci_options"),
    getLanguageVarValue("LANG_neobooking_step_fci_payment"),
  ];

  return (
    <div className="tw-p-5 tw-border tw-relative tw-overflow-hidden tw-font-montserrat">
      <div className="tw-bg-v_blue-light tw-container tw-mx-auto tw-pb-8 tw-relative">
        <div className="tw-flex tw-items-center tw-text-center tw-flex-col tw-mb-6">
          <img
            className="tw-w-[100px] md:tw-w-[170px] tw-mb-6"
            src={getAbsoluteImagePath(
              `/assets/img/customer/Vacanceole-B-RVB.png`
            )}
          ></img>
          <StepTitle
            title={getLanguageVarValue("LANG_neobooking_ehoh")}
            subtitle={getLanguageVarValue("LANG_neobooking_register_online")}
            className=""
          />
        </div>
        <div>
          <p className="tw-font-bold">
            {getLanguageVarValue("LANG_neobooking_fci_intro_essential")}
          </p>
          <ul className="tw-list-inside tw-list-disc tw-mb-2">
            <li>
              {getLanguageVarValue("LANG_neobooking_fci_intro_essential_1")}
            </li>
            <li>
              {getLanguageVarValue("LANG_neobooking_fci_intro_essential_2")}
            </li>
            <li>
              {getLanguageVarValue("LANG_neobooking_fci_intro_essential_3")}
            </li>
            <li>
              {getLanguageVarValue("LANG_neobooking_fci_intro_essential_4")}
            </li>
          </ul>
          <p className="tw-font-bold">
            {getLanguageVarValue("LANG_neobooking_fci_intro_essential_5")}
          </p>
        </div>
        <div className="tw-flex tw-justify-center tw-py-12">
          <Stepper
            alternativeLabel
            activeStep={1}
            connector={<ColorlibConnector />}
            className="tw-flex tw-flex-wrap tw-gap-y-2 tw-w-full md:tw-w-[80%] tw-pb-4 md:tw-pb-0"
          >
            {steps.map((step, index) => {
              return (
                <Step key={`${step}_${index}`} classes={{ root: "!tw-p-0" }}>
                  <StepLabel
                    classes={{ root: "!tw-flex !tw-flex-col" }}
                    icon={
                      <div className="!tw-bg-v_blue !tw-w-[40px] !tw-h-[40px] !tw-flex !tw-items-center !tw-justify-center tw-rounded-full tw-text-white tw-font-spirit tw-text-2 tw-font-bold tw-text-3xl">
                        {index + 1}
                      </div>
                    }
                    StepIconProps={{
                      classes: {
                        active: "activeMuiStep",
                        completed: "!tw-fill-v_blue",
                        root: `rootMuiStep !tw-fill-v_blue !tw-block !tw-w-[40px] !tw-h-[40px]`,
                      },
                    }}
                  >
                    <span className="tw-text-xs tw-text-black sm:tw-text-sm md:tw-text-base tw-font-montserrat">
                      {step}
                    </span>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </div>
        <div className="tw-relative tw-z-10">
          <p className="tw-titre-principal-carrot tw-mb-2">
            {getLanguageVarValue("LANG_neobooking_fci_intro_your_turn")}
          </p>
          <div className="tw-font-montserrat">
            <p className="tw-font-normal tw-text-xl tw-mb-1">
              {getLanguageVarValue("LANG_neobooking_fci_intro_login")}{" "}
            </p>
            <p className="tw-italic">
              {getLanguageVarValue("LANG_neobooking_fci_intro_login_help")}
            </p>
            <p className="tw-mt-1">
              <Popup
                title={getLanguageVarValue(
                  "LANG_neobooking_fci_intro_howto_login"
                )}
                newCancelButton
                btnCloseText={getLanguageVarValue("LANG_neobooking_back")}
                btnElement={(onClick) => (
                  <button
                    type="button"
                    className="tw-outline-none tw-text-sm tw-italic tw-flex tw-items-center tw-gap-x-2"
                    onClick={onClick}
                  >
                    <InfoRounded className="tw-text-v_blue" />
                    <span>
                      {getLanguageVarValue("LANG_neobooking_fci_where_code")}
                    </span>
                  </button>
                )}
              >
                <iframe
                  className="tw-bg-v_blue-light tw-p-4 tw-rounded-[18px]"
                  src={getAbsoluteImagePath(
                    lang.toUpperCase() === "AN"
                      ? "/assets/Connexion_FCI.pdf"
                      : "/assets/Connexion_FCI.pdf"
                  )}
                  width="100%"
                  height="500px"
                >
                  {" "}
                </iframe>
              </Popup>
            </p>
          </div>
          <form onSubmit={handleLoginETOMode}>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-my-8">
              <div className="tw-col-span-2 md:tw-col-span-1">
                <InputText
                  type="text"
                  value={bookingRef}
                  onChange={(e) => setBookingRef(e.target.value.toUpperCase())}
                  additionalClassname="tw-w-full"
                  placeholder="XXXXXXX-JJMMAA-XXX*"
                  required
                />
              </div>
              <div className="tw-col-span-2 md:tw-col-span-1">
                <InputText
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={getLanguageVarValue("LANG_neobooking_email")}
                  additionalClassname="tw-w-full"
                  required
                />
              </div>
            </div>
            <div className="tw-flex tw-justify-center">
              <Button
                variant="danger"
                type="submit"
                disabled={
                  bookingRef?.split("-")?.length !== 3 ||
                  bookingRef?.endsWith("-") ||
                  isEmpty(email) ||
                  loading
                }
                isLoading={loading}
                shape="pills"
                loadingChildren={
                  <>
                    <div>
                      <span>
                        {getLanguageVarValue(
                          "LANG_neobooking_fci_intro_login_i"
                        )}{" "}
                      </span>
                      <span className="tw-font-bold">
                        {getLanguageVarValue(
                          "LANG_neobooking_fci_intro_login_start"
                        )}
                      </span>
                    </div>
                    <svg
                      className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="tw-opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        className="tw-opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </>
                }
                additionalClassname="tw-flex tw-justify-center tw-items-center tw-gap-x-2"
              >
                <div>
                  <span>
                    {getLanguageVarValue("LANG_neobooking_fci_intro_login_i")}{" "}
                  </span>
                  <span className="tw-font-bold">
                    {getLanguageVarValue(
                      "LANG_neobooking_fci_intro_login_start"
                    )}
                  </span>
                </div>
                <ArrowCircleRight />
              </Button>
            </div>
          </form>
        </div>
        <img
          className="tw-absolute tw--top-[15px] tw--left-[20px] tw-max-w-[120px] tw-opacity-60 tw-rotate-[10deg]"
          src={getAbsoluteImagePath("/assets/img/Vacanceole_icons_eyes.svg")}
        />
        <img
          className="tw-absolute tw--bottom-[115px] tw--right-[90px] tw-max-w-[350px] tw-opacity-60 tw--rotate-[20deg] -tw-z-0"
          src={getAbsoluteImagePath("/assets/img/Vacanceole_icons_palmier.svg")}
        />
      </div>
    </div>
  );
};

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "#3e97ef",
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.active && {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  }),
  ...(ownerState.completed && {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
  }),
}));

type FCIStepProps = {
  activeStep: StepEnum;
};

const isValidOccupant = (
  firstName: string,
  lastName: string,
  birthDate: Date
): boolean => {
  return !isEmpty(firstName) && !isEmpty(lastName) && !isEmpty(birthDate);
};

const FCIStep = ({}) => {
  /* Hooks */
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue } = useSession();
  const { cart, customerBooking } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  /* States */
  /* State Step */
  const stepParam: string | null = searchParams.get("step");
  const [activeStep, setActiveStep] = useState<StepEnum>(
    stepParam
      ? !isNaN(parseInt(stepParam))
        ? parseInt(stepParam)
        : StepEnum.RAPPEL
      : StepEnum.RAPPEL
  );
  const steps = [
    getLanguageVarValue("LANG_neobooking_step_fci_auth"),
    getLanguageVarValue("LANG_neobooking_step_fci_info"),
    getLanguageVarValue("LANG_neobooking_step_fci_options"),
    getLanguageVarValue("LANG_neobooking_step_fci_payment"),
  ];
  const [activeStepPayment, setActiveStepPayment] = useState<StepPaymentEnum>(
    StepPaymentEnum.CAUTION
  );
  const [activeStepOption, setActiveStepOption] = useState<StepOptionEnum>(
    StepOptionEnum.GLOBAL
  );

  /* State CGV */
  const [cgvChecked, setCgvChecked] = useState<boolean>(false);
  const [cgvCheckedError, setCgvCheckedError] = useState<boolean>(false);
  const [cgvPaymentCheckedError, setCgvPaymentCheckedError] =
    useState<boolean>(false);

  const [cgvPaymentChecked, setCgvPaymentChecked] = useState<boolean>(false);

  /* State saving */
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    // Mettez à jour l'URL avec le paramètre 'step' chaque fois que 'activeStep' change
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("step", activeStep.toString());

    // Si le paramètre 'step' est déjà présent, mettez à jour sa valeur
    if (stepParam !== null) {
      newSearchParams.set("step", activeStep.toString());
    }

    setSearchParams(newSearchParams);
  }, [activeStep]);

  /* Au chargement du composant, on check le status */
  useEffect(() => {
    const pspParam = searchParams.get("s");
    if (pspParam) {
    }

    let hasId = false;
    if (cart && !isEmpty(cart?.reservations)) {
      hasId = !isEmpty(cart?.reservations?.[0]?.id);
    }

    let badStatus = ["d", "c", "e"];
    let okStatus = ["s"];

    if (pspParam && badStatus.includes(pspParam)) {
      setActiveStep(StepEnum.PAYMENT);
      setActiveStepPayment(StepPaymentEnum.FAIL);
    } else if (pspParam && okStatus.includes(pspParam)) {
      setActiveStep(StepEnum.PAYMENT);
      setActiveStepPayment(StepPaymentEnum.SUCCESS);
    }
  }, []);

  //A chaque changement d'etapes / sous etapes, on remonte en haut de page
  useEffect(() => {
    scrollTop();
  }, [activeStep, activeStepOption, activeStepPayment]);

  /* OLD */
  const { data, loading, error } = useQuery<
    Query,
    QueryHasProductsForOccupantArgs
  >(hasProductsForOccupantQuery(), {
    skip: true,
    variables: {
      session: { name: sessionId },
    },
    fetchPolicy: "no-cache",
  });

  const { data: dataHasProductsForOccupantWithoutTaxes } = useQuery<
    Query,
    QueryHasProductsForOccupantWithoutTaxesArgs
  >(hasProductsForOccupantWithoutTaxesQuery(), {
    variables: {
      session: { name: sessionId },
      input: {
        occupantGuiid:
          cart?.reservations?.[0]?.stays?.[0]?.occupants?.[0]?.guiid!!,
      },
    },
    fetchPolicy: "no-cache",
  });

  const checkOccupant = (): {
    valid: boolean;
    controlToFocus: string | null;
  } => {
    let valid = false;
    let valids: boolean[] = [];
    let controlToFocus: string | null = null;
    cart?.reservations?.forEach((reservation) => {
      reservation?.stays?.forEach((stay) => {
        stay?.occupants?.forEach((occupant) => {
          valid = isValidOccupant(
            occupant?.firstName!!,
            occupant?.lastName!!,
            occupant?.birthDate!!
          );
          if (!valid) {
            valids.push(false);
            if (!controlToFocus) {
              if (isEmpty(occupant?.birthDate))
                controlToFocus = `txt_birthdate_${occupant?.guiid}`;
              if (isEmpty(occupant?.lastName))
                controlToFocus = `txt_lastname_${occupant?.guiid}`;
              if (isEmpty(occupant?.firstName))
                controlToFocus = `txt_firstname_${occupant?.guiid}`;
            }
          }
        });
      });
    });
    let isinvalid = valids.some((x) => x === false);
    return { valid: !isinvalid, controlToFocus };
  };

  const handlePreviousStep = () => {
    switch (activeStep) {
      case StepEnum.RAPPEL:
        //On est sur RAPPEL, on peux pas retourner en arriere, on ne fait rien
        break;
      case StepEnum.COORDONNEES:
        //On est sur COORDONNEES, rien de fou, on peux retourner en arriere, on ne fait rien
        setActiveStep(StepEnum.RAPPEL);
        break;
      case StepEnum.OPTIONS:
        if (activeStepOption === StepOptionEnum.OCCUPANT) {
          setActiveStepOption(StepOptionEnum.GLOBAL);
        } else {
          setActiveStep(StepEnum.COORDONNEES);
        }
        break;
      case StepEnum.PAYMENT:
        if (activeStepPayment === StepPaymentEnum.PAYMENT) {
          setActiveStepPayment(StepPaymentEnum.CAUTION);
        } else if (activeStepPayment === StepPaymentEnum.FAIL) {
          setActiveStepPayment(StepPaymentEnum.PAYMENT);
        } else {
          setActiveStep(StepEnum.OPTIONS);
        }
        break;
    }
  };

  const getWebUser = () => {
    const localWebuser = getLocalStorageItem("webuser");
    return (
      searchParams.get("webuser") ??
      searchParams.get("wu") ??
      localWebuser ??
      process.env.REACT_APP_WEBUSER!!
    );
  };

  const handleNextStep = () => {
    switch (activeStep) {
      case StepEnum.RAPPEL:
        setActiveStep(activeStep + 1);
        break;
      case StepEnum.COORDONNEES:
        const { valid, controlToFocus } = checkOccupant();
        if (!valid) {
          toast.error(
            getLanguageVarValue(
              "LANG_neobooking_complete_all_occupants_details"
            )
          );
          scrollTop();
          focusElementById(controlToFocus);
        } else {
          setActiveStep(activeStep + 1);
        }
        break;
      case StepEnum.OPTIONS:
        if (activeStepOption === StepOptionEnum.GLOBAL) {
          /* NEW */
          if (
            dataHasProductsForOccupantWithoutTaxes &&
            dataHasProductsForOccupantWithoutTaxes.getOccupantProducts &&
            dataHasProductsForOccupantWithoutTaxes.getOccupantProducts
              .occupantProducts
          ) {
            let prestaOccupants = false;
            // Fonction pour vérifier si au moins un produit a un code différent de "TXS" et "TSXV"
            const hasDifferentProductThanMandatory = (
              products: Maybe<Maybe<Product>[]> | undefined
            ): boolean => {
              return products?.some((product) => !product?.mandatory) ?? false;
            };

            for (const occupantProduct of dataHasProductsForOccupantWithoutTaxes
              .getOccupantProducts.occupantProducts) {
              if (hasDifferentProductThanMandatory(occupantProduct?.products)) {
                prestaOccupants = true;
                break;
              }
            }

            if (prestaOccupants) {
              setActiveStepOption(StepOptionEnum.OCCUPANT);
            } else {
              setActiveStep(activeStep + 1);
            }
          } else {
            setActiveStep(activeStep + 1);
          }

          /* OLD */
          /*
          OLD
          if (
            data &&
            data?.hasProductsForOccupant &&
            data?.hasProductsForOccupant?.hasProductsForOccupant &&
            data?.hasProductsForOccupant.hasProductsForOccupant
          ) {
            setActiveStepOption(StepOptionEnum.OCCUPANT);
          } else {
            setActiveStep(activeStep + 1);
          }
          */
        } else {
          setActiveStep(activeStep + 1);
        }
        break;
      case StepEnum.PAYMENT:
        if (activeStepPayment === StepPaymentEnum.CAUTION) {
          if (cgvChecked) {
            setActiveStepPayment(StepPaymentEnum.PAYMENT);
          } else {
            toast.error(
              getLanguageVarValue("LANG_neobooking_fci_cgv_required")
            );
            setCgvCheckedError(true);
            return;
          }
        } else {
          //On est cas page payment StepPaymentEnum.PAYMENT
          if (!cgvPaymentChecked) {
            toast.error(
              getLanguageVarValue("LANG_neobooking_fci_cgv2_required")
            );
            setCgvPaymentCheckedError(true);
            return;
          }
          const reservationUrl =
            window.location.origin +
            `/reservation/pre-checkin?step=${StepEnum.PAYMENT}`;

          const confirmationUrl =
            window.location.origin +
            `/reservation/pre-checkin?step=${StepEnum.PAYMENT}`;

          const reservationId = cart?.reservations!![0]?.id;

          //const acceptUrl = `${confirmationUrl}&s=success&session=${sessionId}&r=${reservationId}&webuser=${getWebUser()}`;
          const acceptUrl = `${confirmationUrl}&s=s&r=${reservationId}&wu=${getWebUser()}`;

          const declineUrl = `${reservationUrl}&s=d&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;
          const cancelUrl = `${reservationUrl}&s=c&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;
          const exceptionUrl = `${reservationUrl}&s=e&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;

          const passNewAmountLowerThanPrevious =
            cart?.reservations?.[0]?.totalAmount?.amount <
            customerBooking?.totalAmount?.amount;

          setIsSaving(true);
          client
            .mutate<Mutation, MutationRedirectPaymentGatewayForCartArgs>({
              mutation: redirectPaymentGatewayForCartMutation(),
              variables: {
                session: { name: sessionId },
                input: {
                  input: {
                    bookingStatus: BookingStatus.Guaranteed,
                    ccardType:
                      cart?.reservations?.[0]?.selectedPaymentCCType ?? 1,
                    paymentMode: "CB",
                    paymentOptions: "CB",
                    automaticPayment: true,
                    url: {
                      acceptUrl,
                      declineUrl,
                      cancelUrl,
                      exceptionUrl,
                    },
                  },
                  passNewAmountLowerThanPrevious:
                    passNewAmountLowerThanPrevious,
                },
              },
            })
            .then((response) => {
              if (
                response.data &&
                response.data.redirectPaymentGatewayForCart &&
                response.data.redirectPaymentGatewayForCart.gateway
              ) {
                setIsSaving(false);
                window.location.href =
                  response.data.redirectPaymentGatewayForCart?.gateway;
              }
            })
            .catch((err) => {
              if (err) {
                toast.error(err.message);
              }
              setIsSaving(false);
            });
        }
        break;
    }
  };

  return (
    <React.Fragment>
      {/* HEADER */}
      <div className="tw-bg-v_blue-light tw-p-3">
        <div className="tw-container tw-mx-auto tw-flex tw-justify-center">
          <Stepper
            alternativeLabel
            activeStep={1}
            connector={<></>}
            className="tw-flex tw-flex-wrap tw-gap-y-2 tw-w-full md:tw-w-[80%] tw-pb-4 md:tw-pb-0"
          >
            {steps.map((step, index) => {
              return (
                <Step key={`${step}_${index}`} classes={{ root: "!tw-p-0" }}>
                  <StepLabel
                    classes={{ root: "!tw-flex !tw-flex-col" }}
                    icon={
                      <div
                        className={clsx(
                          "!tw-w-[40px] !tw-h-[40px] !tw-flex !tw-items-center !tw-justify-center tw-rounded-full tw-text-white tw-font-spirit tw-text-2 tw-font-bold tw-text-3xl",
                          index === activeStep
                            ? "tw-bg-v_blue"
                            : index < activeStep
                            ? "tw-bg-v_blue-dark"
                            : "tw-bg-[#E4E4E4]",
                          searchParams.get("s") &&
                            activeStep === StepEnum.PAYMENT &&
                            activeStepPayment > StepPaymentEnum.PAYMENT
                            ? "tw-bg-v_blue-dark"
                            : ""
                        )}
                      >
                        {index + 1}
                      </div>
                    }
                    StepIconProps={{
                      classes: {
                        active: "activeMuiStep",
                        completed: "!tw-fill-v_blue",
                        root: `rootMuiStep !tw-fill-v_blue !tw-block !tw-w-[40px] !tw-h-[40px]`,
                      },
                    }}
                  >
                    <span className="tw-text-xs tw-text-v_blue-dark sm:tw-text-sm md:tw-text-base tw-font-montserrat">
                      {step}
                    </span>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </div>
      </div>
      {/* CONTENT */}
      <div className="tw-py-8">
        {activeStep === StepEnum.RAPPEL ? (
          <div className="tw-container md:tw-max-w-4xl tw-mx-auto ">
            <StepFCIRappel />
          </div>
        ) : activeStep === StepEnum.COORDONNEES ? (
          <div className="tw-container tw-mx-auto">
            <StepFCICoordonnee />
          </div>
        ) : activeStep === StepEnum.OPTIONS ? (
          <div className="tw-container tw-mx-auto">
            <StepFCIOptions activeStepOption={activeStepOption} />
          </div>
        ) : activeStep === StepEnum.PAYMENT ? (
          <StepFCIPayment
            cgvChecked={cgvChecked}
            setCgvChecked={setCgvChecked}
            cgvCheckedError={cgvCheckedError}
            setCgvCheckedError={setCgvCheckedError}
            cgvPaymentCheckedError={cgvPaymentCheckedError}
            setCgvPaymentCheckedError={setCgvPaymentCheckedError}
            cgvPaymentChecked={cgvPaymentChecked}
            setCgvPaymentChecked={setCgvPaymentChecked}
            activeStep={activeStep}
            activeStepPayment={activeStepPayment}
            onPreviousStep={handlePreviousStep}
          />
        ) : (
          <></>
        )}
      </div>
      {/* FOOTER */}
      {activeStepPayment !== StepPaymentEnum.FAIL &&
      activeStepPayment !== StepPaymentEnum.SUCCESS ? (
        <div className="tw-container tw-mx-auto tw-pb-8">
          <div className="tw-flex tw-justify-around">
            {activeStep !== StepEnum.RAPPEL ? (
              <Button
                shape="pills"
                variant="danger"
                onClick={handlePreviousStep}
                additionalClassname="tw-w-auto tw-flex tw-justify-center tw-items-center tw-gap-x-2 tw-font-bold"
              >
                <ArrowCircleLeft />
                {getLanguageVarValue("LANG_neobooking_back")}
              </Button>
            ) : (
              <></>
            )}
            <Button
              shape="pills"
              variant="danger"
              isLoading={isSaving}
              loadingChildren={
                <>
                  {getLanguageVarValue("LANG_neobooking_next_step")}
                  <LoadingDots />
                </>
              }
              onClick={handleNextStep}
              additionalClassname="tw-w-auto tw-flex tw-justify-center tw-items-center tw-gap-x-2 tw-font-bold"
            >
              {getLanguageVarValue("LANG_neobooking_next_step")}
              <ArrowCircleRight />
            </Button>
          </div>
        </div>
      ) : (
        <></>
      )}
    </React.Fragment>
  );
};

const StepFCICoordonnee = () => {
  const { getLanguageVarValue, funnel_type } = useSession();
  const { cart } = useCart();

  const canAddOccupant = useMemo(() => {
    const isVACAF =
      cart?.reservations?.[0]?.contractFolder?.cmsCriterias?.find(
        (x) => x?.code === "AFCI"
      )?.value === "1";
    return isVACAF ? false : true;
  }, []);

  return (
    <React.Fragment>
      <StepTitle
        className="tw-mb-4"
        title="Pssst"
        subtitle={getLanguageVarValue("LANG_neobooking_getting_to_know_you")}
      />
      <p className="tw-font-montserrat tw-mb-6">
        {getLanguageVarValue("LANG_neobooking_fci_auth_intro")}
      </p>
      <div>
        {cart?.reservations?.map((reservation, reservationIndex) => {
          return (
            reservation && (
              <div key={`reservation_${reservation?.id}_${reservationIndex}`}>
                <div>
                  <ReservationOccupants
                    btnAddOccupantText={getLanguageVarValue(
                      "LANG_neobooking_occupant_add"
                    )}
                    loadingAddingOccupantText={getLanguageVarValue(
                      "LANG_neobooking_occupant_adding_in_progress"
                    )}
                    successfullyOccupantAddedText={getLanguageVarValue(
                      "LANG_neobooking_occupant_successfully_added"
                    )}
                    productListTitle={getLanguageVarValue(
                      "LANG_neobooking_product_global_title"
                    )}
                    reservation={reservation}
                    btnAddBabyText={getLanguageVarValue(
                      "LANG_neobooking_occupant_baby_add"
                    )}
                    successfullyBabyAddedText={getLanguageVarValue(
                      "LANG_neobooking_occupant_baby_successfully_added"
                    )}
                    loadingAddingBabyText={getLanguageVarValue(
                      "LANG_neobooking_occupant_baby_adding_in_progress"
                    )}
                    showProductOccupant={false}
                    showAddOccupant={canAddOccupant}
                    showTitle={false}
                    showValidity={true}
                    columnReverse={true}
                    disableEditFirstOccupant={true}
                  />
                </div>
              </div>
            )
          );
        })}
      </div>
    </React.Fragment>
  );
};

type StepFCIOptionsProps = {
  activeStepOption?: StepOptionEnum;
};
const StepFCIOptions: React.FC<StepFCIOptionsProps> = ({
  activeStepOption,
}) => {
  const { getLanguageVarValue, funnel_type } = useSession();
  const { cart } = useCart();

  return (
    <React.Fragment>
      <StepTitle
        className="tw-mb-4"
        title="Wahou"
        subtitle={
          activeStepOption === StepOptionEnum.GLOBAL
            ? getLanguageVarValue("LANG_neobooking_fci_options_global")
            : getLanguageVarValue("LANG_neobooking_fci_options_occupants")
        }
      />
      {activeStepOption === StepOptionEnum.GLOBAL ? (
        <React.Fragment>
          <div className="tw-font-montserrat tw-mb-6">
            <p>
              {getLanguageVarValue("LANG_neobooking_fci_options_global_intro")}
            </p>
            <p>
              {getLanguageVarValue(
                "LANG_neobooking_fci_options_global_intro_2"
              )}
            </p>
          </div>
          <StepPrestation showTitle={false} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <div>
            <p className="tw-font-montserrat tw-mb-6">
              {getLanguageVarValue(
                "LANG_neobooking_fci_options_occupants_intro"
              )}
            </p>
            <div>
              {cart?.reservations?.map((reservation, reservationIndex) => {
                return (
                  reservation && (
                    <div
                      key={`reservation_${reservation?.id}_${reservationIndex}`}
                    >
                      <div>
                        <ReservationOccupants
                          btnAddOccupantText={getLanguageVarValue(
                            "LANG_neobooking_occupant_add"
                          )}
                          loadingAddingOccupantText={getLanguageVarValue(
                            "LANG_neobooking_occupant_adding_in_progress"
                          )}
                          successfullyOccupantAddedText={getLanguageVarValue(
                            "LANG_neobooking_occupant_successfully_added"
                          )}
                          productListTitle={getLanguageVarValue(
                            "LANG_neobooking_product_global_title"
                          )}
                          reservation={reservation}
                          btnAddBabyText={getLanguageVarValue(
                            "LANG_neobooking_occupant_baby_add"
                          )}
                          successfullyBabyAddedText={getLanguageVarValue(
                            "LANG_neobooking_occupant_baby_successfully_added"
                          )}
                          loadingAddingBabyText={getLanguageVarValue(
                            "LANG_neobooking_occupant_baby_adding_in_progress"
                          )}
                          showProductOccupant={true}
                          showAddOccupant={false}
                          showTitle={false}
                          showInfoOccupant={false}
                          showValidity={true}
                          columnReverse={true}
                        />
                      </div>
                    </div>
                  )
                );
              })}
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const StepFCIRappel = () => {
  const { cart } = useCart();
  const { getLanguageVarValue } = useSession();
  return (
    <React.Fragment>
      <div className="tw-mb-6">
        <div className="tw-titre-principal-spirit !tw-font-light">
          {getLanguageVarValue("LANG_neobooking_fci_rappel_thanks")}
        </div>
        <div className="tw-titre-principal-spirit">
          {cart?.reservations?.[0]?.property?.label}
        </div>
      </div>
      {/* Content */}
      <div className="tw-flex tw-justify-between tw-font-montserrat tw-mb-4">
        <div className="tw-w-2/3">
          <div className="tw-text-v_blue tw-font-semibold">
            <PinDropOutlined /> {getLanguageVarValue("LANG_neobooking_address")}
          </div>
          <div>
            {!isEmpty(cart?.reservations?.[0]?.property?.address?.streetName)
              ? cart?.reservations?.[0]?.property?.address?.streetName
              : cart?.reservations?.[0]?.property?.address
                  ?.addressComplementary1}
          </div>
          <div>
            {cart?.reservations?.[0]?.property?.address?.zipCode}{" "}
            {cart?.reservations?.[0]?.property?.address?.city}
          </div>
          <div className="tw-uppercase">
            {cart?.reservations?.[0]?.property?.address?.addressCountry?.label}
          </div>
        </div>
        <div className="tw-text-left tw-w-1/3">
          <div className="tw-text-v_blue tw-font-semibold">
            <PhoneOutlined /> {getLanguageVarValue("LANG_neobooking_adr_tel_1")}
          </div>
          <div>{cart?.reservations?.[0]?.property?.address?.phone1}</div>
        </div>
      </div>
      {/* Details */}
      <div className="tw-rounded-full tw-bg-v_blue-light tw-text-v_blue-dark tw-p-6 tw-relative tw-mb-6">
        <div className="tw-flex tw-gap-x-4 tw-justify-start md:tw-justify-between tw-items-center">
          <div className="tw-border-r-v_blue-dark tw-font-spirit tw-text-center md:tw-text-left">
            <span className="tw-font-bold">
              {getLanguageVarValue("LANG_neobooking_details")}
            </span>{" "}
            {getLanguageVarValue("LANG_neobooking_of_your_stay")}
          </div>
          <div className="tw-border tw-border-v_blue-dark tw-h-[40px]"></div>
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-x-2 tw-font-montserrat">
            <div>
              {dateFormat(cart?.reservations?.[0]?.startDate, "dd/mm/yy")}{" "}
              {getLanguageVarValue("LANG_neobooking_to")}{" "}
              {dateFormat(cart?.reservations?.[0]?.endDate, "dd/mm/yy")} -{" "}
              {cart?.reservations?.[0]?.nbDays}{" "}
              {getLanguageVarValue(
                (cart?.reservations?.[0]?.nbDays ?? 1) <= 1
                  ? "LANG_neobooking_day"
                  : "LANG_neobooking_days"
              )}
            </div>
            <div className="tw-hidden md:tw-block">-</div>
            <div className="tw-lowercase">
              <NbOccupantByReservationTitle
                reservation={cart?.reservations?.[0]!!}
                pluralAdultText={getLanguageVarValue("LANG_neobooking_adults")}
                singleAdultText={getLanguageVarValue("LANG_neobooking_adult")}
                pluralChildrenText={getLanguageVarValue(
                  "LANG_neobooking_childrens"
                )}
                singleChildrenText={getLanguageVarValue(
                  "LANG_neobooking_children"
                )}
                pluralBabyText={getLanguageVarValue("LANG_neobooking_babies")}
                singleBabyText={getLanguageVarValue("LANG_neobooking_baby")}
              />
            </div>
          </div>
        </div>
        <img
          className="tw-w-[65px] tw-absolute tw-opacity-60 tw--top-[55px] tw-right-[25%] md:tw-right-[15%]"
          src={getAbsoluteImagePath("/assets/img/Vacanceole_icons_tippy.svg")}
        ></img>
      </div>
      {/* Sumary */}
      <div className="tw-font-montserrat">
        <RecapLineItems showTotal={false} cart={cart} />
      </div>
      <hr className="tw-my-3 " />
      {/* Total paid */}
      <div className="tw-text-right tw-font-montserrat tw-mb-6">
        {getLanguageVarValue("LANG_neobooking_fci_total_paid")}{" "}
        <span className="tw-font-bold">
          {getTotalPaid(cart).toFixed(2)}
          {getCurrencySymbol(
            cart?.reservations?.[0]?.totalAmount?.currencyCode
          )}
        </span>
      </div>
      {/* Phrase info */}
      <div className="tw-flex tw-justify-center">
        <p className="tw-w-2/3 tw-text-center tw-text-v_red-dark tw-font-montserrat">
          {getLanguageVarValue("LANG_neobooking_fci_rappel_intro_1")}{" "}
          <span className="tw-font-bold">
            {getLanguageVarValue("LANG_neobooking_fci_rappel_intro_2")}
          </span>
        </p>
      </div>
    </React.Fragment>
  );
};

type StepFCIPaymentProps = {
  cgvChecked: boolean;
  setCgvChecked: (value: boolean) => void;
  cgvCheckedError: boolean;
  setCgvCheckedError: (value: boolean) => void;
  cgvPaymentCheckedError: boolean;
  setCgvPaymentCheckedError: (value: boolean) => void;
  cgvPaymentChecked: boolean;
  setCgvPaymentChecked: (value: boolean) => void;
  activeStep?: StepEnum;
  activeStepPayment?: StepPaymentEnum;
  onPreviousStep: () => void;
};

const StepFCIPayment: React.FC<StepFCIPaymentProps> = ({
  cgvChecked,
  setCgvChecked,
  cgvCheckedError,
  setCgvCheckedError,
  cgvPaymentChecked,
  setCgvPaymentChecked,
  cgvPaymentCheckedError,
  setCgvPaymentCheckedError,
  activeStepPayment,
  activeStep,
  onPreviousStep,
}) => {
  return activeStepPayment === StepPaymentEnum.CAUTION ? (
    <div className="tw-container tw-mx-auto">
      <StepFCIPaymentIntro
        cgvChecked={cgvChecked}
        setCgvChecked={setCgvChecked}
        cgvCheckedError={cgvCheckedError}
        setCgvCheckedError={setCgvCheckedError}
      />
    </div>
  ) : activeStepPayment === StepPaymentEnum.PAYMENT ? (
    <StepFCIPaymentContent
      cgvPaymentChecked={cgvPaymentChecked}
      setCgvPaymentChecked={setCgvPaymentChecked}
      cgvPaymentCheckedError={cgvPaymentCheckedError}
      setCgvPaymentCheckedError={setCgvPaymentCheckedError}
      activeStep={activeStep}
    />
  ) : activeStepPayment === StepPaymentEnum.FAIL ? (
    <div className="tw-container tw-mx-auto">
      <StepFCIPaymentFail onPreviousStep={onPreviousStep} />
    </div>
  ) : activeStepPayment === StepPaymentEnum.SUCCESS ? (
    <div className="tw-container tw-mx-auto">
      <StepFCIPaymentSuccess />
    </div>
  ) : (
    <></>
  );
};

type StepFCIPaymentIntroProps = {
  cgvChecked: boolean;
  setCgvChecked: (value: boolean) => void;
  cgvCheckedError: boolean;
  setCgvCheckedError: (value: boolean) => void;
};

const StepFCIPaymentIntro: React.FC<StepFCIPaymentIntroProps> = ({
  cgvChecked,
  setCgvChecked,
  cgvCheckedError,
  setCgvCheckedError,
}) => {
  const { getLanguageVarValue, lang } = useSession();

  return (
    <React.Fragment>
      <StepTitle
        className=""
        subtitle={getLanguageVarValue(
          "LANG_neobooking_fci_payment_intro_title"
        )}
      />
      <div className="tw-my-8">
        <Encart title="Caution">
          <div className="tw-flex tw-flex-col tw-gap-y-3">
            <p className="tw-font-bold">
              {getLanguageVarValue("LANG_neobooking_fci_payment_intro_1")}
            </p>
            <p>
              {getLanguageVarValue("LANG_neobooking_fci_payment_intro_2")}{" "}
              <span className="tw-font-bold">
                {getLanguageVarValue(
                  "LANG_neobooking_fci_payment_intro_2_bold"
                )}
              </span>
            </p>
            <p>
              <span className="tw-font-bold">
                {getLanguageVarValue(
                  "LANG_neobooking_fci_payment_intro_3_bold"
                )}
              </span>{" "}
              {getLanguageVarValue("LANG_neobooking_fci_payment_intro_3")}{" "}
              <span className="tw-font-bold">
                {getLanguageVarValue(
                  "LANG_neobooking_fci_payment_intro_3_bold_2"
                )}
              </span>
            </p>
            <p>
              {getLanguageVarValue("LANG_neobooking_fci_payment_intro_cgv")}{" "}
              <a
                href={
                  lang.toUpperCase() === "AN"
                    ? "https://www.calameo.com/vacanceole/read/00576586231c5c2c04c04?authid=AntUZnQvjYAt"
                    : "https://www.calameo.com/vacanceole/read/00576586210e2b994f693?authid=KrGDi3vW0b0c"
                }
                target="_blank"
                className="tw-underline"
              >
                {getLanguageVarValue("LANG_neobooking_fci_payment_cgv")}.
              </a>
            </p>
          </div>
        </Encart>
      </div>
      <div
        className={clsx(
          cgvCheckedError ? "tw-border tw-border-v_red-dark" : ""
        )}
      >
        <InputCheckbox
          id="cgvRef"
          label={getLanguageVarValue("LANG_neobooking_step_payment_read_cgv")}
          checked={cgvChecked}
          onChange={() => {
            setCgvChecked(!cgvChecked);
            setCgvCheckedError(false);
          }}
        />
      </div>
    </React.Fragment>
  );
};

type StepFCIPaymentContentProps = {
  cgvPaymentChecked: boolean;
  setCgvPaymentChecked: (value: boolean) => void;
  cgvPaymentCheckedError: boolean;
  setCgvPaymentCheckedError: (value: boolean) => void;
  activeStep?: StepEnum;
};
const StepFCIPaymentContent: React.FC<StepFCIPaymentContentProps> = ({
  cgvPaymentChecked,
  setCgvPaymentChecked,
  cgvPaymentCheckedError,
  setCgvPaymentCheckedError,
  activeStep,
}) => {
  /* Hooks */
  const { getLanguageVarValue, lang, funnel_type } = useSession();
  const { cart, updateCartPaymentMethod } = useCart();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | undefined
  >(cart?.reservations?.[0]?.selectedPaymentMethod ?? undefined);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<
    string | undefined
  >(cart?.reservations?.[0]?.selectedPaymentCCType?.toString() ?? undefined);

  useEffect(() => {
    if (selectedPaymentMethod) {
      const paymentOption =
        selectedPaymentOption === "28" ? "1" : selectedPaymentOption;

      updateCartPaymentMethod(
        BookingStatus.Guaranteed,
        paymentOption ? parseInt(paymentOption) : 1,
        selectedPaymentMethod ? selectedPaymentMethod : "CB",
        (err) => {
          if (err) toast.error(err.message);
        }
      );

      if (paymentOption !== selectedPaymentOption) {
        setSelectedPaymentOption(paymentOption);
      }
    }
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (selectedPaymentOption) {
      const paymentOption =
        selectedPaymentOption === "28" ? "1" : selectedPaymentOption;

      updateCartPaymentMethod(
        BookingStatus.Guaranteed,
        paymentOption ? parseInt(paymentOption) : 1,
        selectedPaymentMethod ? selectedPaymentMethod : "CB",
        (err) => {
          if (err) toast.error(err.message);
        }
      );

      if (paymentOption !== selectedPaymentOption) {
        setSelectedPaymentOption(paymentOption);
      }
    }
  }, [selectedPaymentOption]);

  /* Functions */
  const handleSelectedPaymentOption = (option: string | undefined) => {
    if (option) {
      const paymentOption = option === "28" ? "1" : option;
      setSelectedPaymentOption(paymentOption);
    }
  };

  /* Render */
  return (
    <React.Fragment>
      <div className="tw-container tw-mx-auto">
        <div className="tw-mb-6">
          <p className="tw-titre-principal-spirit !tw-font-semibold tw-text-v_blue-dark tw-mb-2">
            {getLanguageVarValue("LANG_neobooking_fci_recap")}
          </p>
          <p className="tw-font-montserrat">
            {getLanguageVarValue("LANG_neobooking_fci_payment_finish")}
          </p>
        </div>
      </div>
      <div className="tw-bg-v_blue-light">
        <div className="tw-container tw-mx-auto tw-py-4">
          <RecapLineItems
            showLineitems={true}
            showTotal={false}
            cart={cart}
            activeStep={activeStep}
          />
        </div>
      </div>
      <div className="tw-container tw-mx-auto">
        <RecapLineItems
          showLineitems={false}
          showHr={false}
          showRemaining={false}
          showTotal={true}
          cart={cart}
          activeStep={activeStep}
        />
        <div className="tw-rounded-[18px] tw-bg-v_blue-light tw-py-4 tw-px-2 tw-font-montserrat tw-mt-6">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div>
              {getLanguageVarValue("LANG_neobooking_fci_recap_total_remaining")}
            </div>
            <div className="tw-font-bold tw-text-v_red-dark">
              {getTotalRemaining(cart)}
              {getCurrencySymbol(
                cart?.reservations?.[0]?.totalAmount?.currencyCode
              )}
            </div>
          </div>
        </div>
        <div>
          <SelectPaymentMethods
            automaticPaymentOnly
            selected={selectedPaymentMethod}
            setSelected={setSelectedPaymentMethod}
            selectedPaymentOption={selectedPaymentOption}
            setSelectedPaymentOption={handleSelectedPaymentOption}
            hideANCV={true}
          />
        </div>
        <div className="tw-font-montserrat tw-my-4 tw-text-sm">
          {getLanguageVarValue("LANG_neobooking_fci_payment_caution_info")}
        </div>
        <div
          className={clsx(
            cgvPaymentCheckedError ? "tw-border tw-border-v_red-dark" : ""
          )}
        >
          <InputCheckbox
            id="cgvPaymentRef"
            label={
              <>
                <span>
                  {getLanguageVarValue("LANG_neobooking_check_terms_1")}
                </span>{" "}
                <a
                  href={
                    lang.toUpperCase() === "AN"
                      ? "https://www.calameo.com/vacanceole/read/00576586231c5c2c04c04?authid=AntUZnQvjYAt"
                      : "https://www.calameo.com/vacanceole/read/00576586210e2b994f693?authid=KrGDi3vW0b0c"
                  }
                  target="_blank"
                  className="tw-underline tw-outline-none"
                >
                  {getLanguageVarValue("LANG_neobooking_fci_payment_cgv")}.
                </a>
              </>
            }
            checked={cgvPaymentChecked}
            onChange={() => {
              setCgvPaymentChecked(!cgvPaymentChecked);
              setCgvPaymentCheckedError(false);
            }}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

type StepFCIPaymentFailProps = {
  onPreviousStep: () => void;
};
const StepFCIPaymentFail: React.FC<StepFCIPaymentFailProps> = ({
  onPreviousStep,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();

  /* Render */
  return (
    <React.Fragment>
      <div className="tw-mb-16">
        <StepTitle
          className="tw-mb-4"
          title={getLanguageVarValue("LANG_neobooking_fci_payment_error_title")}
          subtitle={getLanguageVarValue(
            "LANG_neobooking_fci_payment_error_subtitle"
          )}
        />
        <div className="tw-font-montserrat">
          <p className="tw-mb-6">
            {getLanguageVarValue("LANG_neobooking_fci_payment_error_msg_1")}
          </p>
          <p>
            {getLanguageVarValue("LANG_neobooking_fci_payment_error_msg_2")}
          </p>
        </div>
      </div>
      <div className="tw-flex tw-justify-center">
        <Button
          shape="pills"
          variant="danger"
          onClick={onPreviousStep}
          additionalClassname="tw-w-auto tw-flex tw-justify-center tw-items-center tw-gap-x-2 tw-font-bold"
        >
          <ArrowCircleLeft />
          {getLanguageVarValue("LANG_neobooking_fci_payment_error_btn_back")}
        </Button>
      </div>
    </React.Fragment>
  );
};

type StepFCIPaymentSuccessProps = {};

const CODE_ARRIVE = "ARRW";

const StepFCIPaymentSuccess: React.FC<StepFCIPaymentSuccessProps> = () => {
  /* Hooks */
  const client = useApolloClient();
  const { cart, loading, clear } = useCart();
  const [searchParams] = useSearchParams();
  const { sessionId, getLanguageVarValue, funnel_type } = useSession();

  /* States */
  const [cartState, setCardState] = useState<Cart | null>(null);
  const [options, setOptions] = useState<SelectOptionType[]>([]);
  const [choices, setChoices] = useState<Maybe<Option>[]>([]);
  const [selectedChoice, setSelectedChoice] = useState<Option | null>(null);
  const [loadingFinalise, setLoadingFinalise] = useState<boolean>(false);

  /* Effects */
  useEffect(() => {
    if (!loading) {
      if (cart) {
        const pspStatus = searchParams.get("s");
        if (pspStatus) {
          if ((pspStatus as PspCallbackStatus) === "s") {
            setCardState(cart);
            clear((err) => {}, false);
          }
        } else {
          window.location.href = window.location.origin;
        }
      }
    }

    const asyncExpire = async () => {
      removeLocalStorageItem("sessionId");
    };

    const asyncExpireOLD = async () => {
      //On expire la session + clear localstorage session
      const { data: dataExpireSession } = await client.mutate<
        Mutation,
        MutationExpireSessionArgs
      >({
        mutation: expireSessionMutation(),
        variables: {
          session: { name: sessionId },
        },
        fetchPolicy: "no-cache",
      });
      if (
        dataExpireSession &&
        dataExpireSession.expireSession &&
        dataExpireSession.expireSession.isExpired &&
        dataExpireSession.expireSession.isExpired
      ) {
        removeLocalStorageItem("sessionId");
      }
    };

    return () => {
      asyncExpire();
    };
  }, [loading]);

  const initHoraires = () => {
    client
      .query<Query, QueryGetBookingRecordCriteriasArgs>({
        query: getBookingRecordCriteriasQuery(),
        variables: {
          session: { name: sessionId },
        },
      })
      .then((response) => {
        if (
          response &&
          response.data &&
          response.data.getBookingRecordCriterias
        ) {
          let choices = response.data.getBookingRecordCriterias.criterias?.find(
            (x) => x?.code === CODE_ARRIVE
          );

          if (choices) {
            const orderedChoices = sortBy(
              (choices?.choiceList as Option[]) ?? [],
              (x) => parseInt(x?.code as string)
            );

            const options = orderedChoices?.map((x) => {
              return {
                label: x?.label,
                value: x?.code,
              } as SelectOptionType;
            });
            setSelectedChoice(orderedChoices[0]);
            setChoices(orderedChoices);
            setOptions(options);
          }
        }
      });
  };

  const handleChangeChoice = (code: string) => {
    const selectedOption = choices.find((x) => x?.code === code);
    if (selectedOption) {
      setSelectedChoice(selectedOption);
    }
  };

  //Click bouton "je finalise"
  const handleSubmitChoice = async () => {
    removeLocalStorageItem("sessionId");

    window.location.href =
      process.env.REACT_APP_CUSTOMER_URL ?? "https://www.vacanceole.com/";
  };

  const handleSubmitChoiceOLD = async () => {
    setLoadingFinalise(true);

    if (selectedChoice) {
      //On ajoute la resa du client au panier car clear précedement
      try {
        const { data: dataUnlock } = await client.mutate<
          Mutation,
          MutationUnlockBookingArgs
        >({
          mutation: unlockBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: cartState?.reservations?.[0]?.id ?? 0,
            },
          },
          fetchPolicy: "no-cache",
        });

        const { data, errors } = await client.mutate<
          Mutation,
          MutationUpdateCustomerBookingArgs
        >({
          mutation: updateCustomerBookingMutation(),
          variables: {
            session: { name: sessionId },
            input: {
              bookingId: cartState?.reservations?.[0]?.id ?? 0,
            },
          },
          fetchPolicy: "no-cache",
        });

        //Si ok, on MAJ le critère updateBookingCriteriasM
        if (
          data &&
          data.updateCustomerBooking &&
          data.updateCustomerBooking.cart
        ) {
          client
            .mutate<Mutation, MutationUpdateBookingCriteriasArgs>({
              mutation: updateBookingCriteriasMutation(),
              variables: {
                session: { name: sessionId },
                input: {
                  criterias: [
                    {
                      code: CODE_ARRIVE,
                      value: selectedChoice?.code!!,
                    },
                  ],
                },
              },
              fetchPolicy: "no-cache",
            })
            .then(async (response) => {
              if (
                response &&
                response.data &&
                response.data.updateBookingCriterias &&
                response.data.updateBookingCriterias.cart
              ) {
                const { data: dataExpireSession } = await client.mutate<
                  Mutation,
                  MutationExpireSessionArgs
                >({
                  mutation: expireSessionMutation(),
                  variables: {
                    session: { name: sessionId },
                  },
                  fetchPolicy: "no-cache",
                });
                setLoadingFinalise(false);
                if (
                  dataExpireSession &&
                  dataExpireSession.expireSession &&
                  dataExpireSession.expireSession.isExpired &&
                  dataExpireSession.expireSession.isExpired
                ) {
                  removeLocalStorageItem("sessionId");
                }

                window.location.href =
                  process.env.REACT_APP_CUSTOMER_URL ??
                  "https://www.vacanceole.com/";
              }
            })
            .catch((err) => {
              toast.error(err.message);
            })
            .finally(() => {
              setLoadingFinalise(false);
            });
        }
      } catch (error) {
        if (error instanceof ApolloError) {
          toast.error(error.message);
        }
        setLoadingFinalise(false);
      }
    } else {
      //todo expire session
      const { data: dataExpireSession } = await client.mutate<
        Mutation,
        MutationExpireSessionArgs
      >({
        mutation: expireSessionMutation(),
        variables: {
          session: { name: sessionId },
        },
        fetchPolicy: "no-cache",
      });
      if (
        dataExpireSession &&
        dataExpireSession.expireSession &&
        dataExpireSession.expireSession.isExpired &&
        dataExpireSession.expireSession.isExpired
      ) {
        removeLocalStorageItem("sessionId");
      }
      setLoadingFinalise(false);
      window.location.href =
        process.env.REACT_APP_CUSTOMER_URL ?? "https://www.vacanceole.com/";
    }
  };

  /* Render */
  if (loading) return <LoadingScreen />;

  return (
    <React.Fragment>
      <div className="tw-mb-16">
        <StepTitle
          className="tw-mb-4"
          title={getLanguageVarValue(
            "LANG_neobooking_fci_payment_success_title"
          )}
          subtitle={getLanguageVarValue(
            "LANG_neobooking_fci_payment_success_subtitle"
          )}
        />
        <div className="tw-font-montserrat tw-mb-6">
          <p>
            {getLanguageVarValue("LANG_neobooking_fci_payment_success_msg_1")}
          </p>
        </div>
        {/*
          <div>
            <div className="tw-titre-principal-spirit tw-mb-4">
              {getLanguageVarValue("LANG_neobooking_fci_payment_one_minute")}
            </div>
            <p className="tw-font-montserrat tw-mb-2">
              {getLanguageVarValue("LANG_neobooking_fci_payment_when")}
            </p>
            <div className="tw-w-[200px] tw-mb-6">
              <Select
                onChange={(e) => handleChangeChoice(e.target.value)}
                options={options}
                additionalClassname="tw-w-auto"
              />
            </div>
            {selectedChoice &&
            !isNaN(parseInt(selectedChoice.code!!)) &&
            parseInt(selectedChoice.code!!) >= 19 ? (
              <p className="tw-font-bold tw-font-montserrat tw-text-v_red-dark">
                {getLanguageVarValue(
                  "LANG_neobooking_fci_payment_late_horaire"
                )}
              </p>
            ) : (
              <></>
            )}
          </div>
          */}
        <div className="tw-flex tw-font-montserrat tw-gap-x-24 tw-mt-6">
          <div className="tw-text-left">
            <div className="tw-text-v_blue tw-font-semibold">
              <PhoneOutlined />{" "}
              {getLanguageVarValue("LANG_neobooking_adr_tel_1")}
            </div>
            <div>{cart?.reservations?.[0]?.property?.label}</div>
            <div>{cart?.reservations?.[0]?.property?.address?.phone1}</div>
          </div>
          <div className="tw-text-left">
            <div className="tw-text-v_blue tw-font-semibold">
              <PhoneOutlined />{" "}
              {getLanguageVarValue("LANG_neobooking_fci_your_stay")}
            </div>
            <div>
              {getLanguageVarValue(
                "LANG_neobooking_fci_payment_success_arrive_at"
              )}
            </div>
            <div>
              {getLanguageVarValue(
                "LANG_neobooking_fci_payment_success_leave_at"
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="tw-flex tw-justify-center">
        <Button
          shape="pills"
          isLoading={loadingFinalise}
          variant="danger"
          type="button"
          additionalClassname="tw-w-auto tw-font-bold tw-min-w-[200px] tw-flex tw-gap-x-2 tw-justify-center tw-items-center"
          onClick={handleSubmitChoice}
        >
          {getLanguageVarValue(
            /*"LANG_neobooking_fci_payment_success_btn_back"*/ "LANG_neobooking_back_to_website"
          )}
          <ArrowCircleRight />
        </Button>
      </div>
    </React.Fragment>
  );
};

type RecapLineItemsProps = {
  cart: Cart | null;
  showLineitems?: boolean;
  showTotal?: boolean;
  showRemaining?: boolean;
  showHr?: boolean;
  activeStep?: StepEnum;
};
const RecapLineItems: React.FC<RecapLineItemsProps> = ({
  cart,
  showLineitems = true,
  showTotal = true,
  showRemaining = true,
  showHr = true,
  activeStep,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();

  /* Render */
  return (
    <React.Fragment>
      {showLineitems && showLineitems ? (
        <div className="tw-flex tw-flex-col tw-gap-y-4 tw-font-montserrat">
          <div className="tw-flex tw-flex-col">
            <div>
              <DistributionBase cart={cart} />
            </div>
            <div className="tw-text-sm">{getFormuleSejour(cart)}</div>
          </div>
          {getAllLineItems(cart)?.map((lineitem) => {
            return lineitem?.product?.isBase === false &&
              lineitem?.preReserved === false &&
              !COMMISSIONS_PRODUCTS.includes(lineitem?.product?.code) && //On masque les commissions
              !TAXES_PRODUCTS.includes(lineitem?.product?.code) ? (
              <div className="tw-flex tw-justify-between">
                <div>
                  <span className="tw-font-bold">{lineitem?.quantity} x</span>{" "}
                  {lineitem?.label}
                </div>
                <div className="tw-font-bold">
                  {lineitem?.totalAmount?.amount?.toFixed(2)}
                  {getCurrencySymbol(lineitem?.totalAmount?.currencyCode)}
                </div>
              </div>
            ) : (
              <></>
            );
          })}

          {activeStep && activeStep === StepEnum.PAYMENT ? (
            <div className="tw-flex tw-justify-between">
              <div>{getLanguageVarValue("LANG_neobooking_total_tax")}</div>
              <div className="tw-font-bold">
                {getTotalTax(cart)?.toFixed(2)}
                {getCurrencySymbol(
                  cart?.reservations?.[0]?.totalAmount?.currencyCode
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
      {showTotal && showTotal ? (
        <div className="tw-flex tw-flex-col tw-gap-y-4 tw-font-montserrat tw-mt-4">
          {showHr && showHr ? <hr className="tw-border-v_blue-dark" /> : <></>}
          <p className="tw-text-v_red-dark tw-font-bold">
            {getLanguageVarValue("LANG_neobooking_totaux")}
          </p>
          <div className="tw-flex tw-justify-between tw-items-center">
            <div>
              {getLanguageVarValue("LANG_neobooking_fci_recap_total_amount")}
            </div>
            <div className="tw-font-bold">
              {cart?.reservations?.[0]?.totalAmount?.amount}
              {getCurrencySymbol(
                cart?.reservations?.[0]?.totalAmount?.currencyCode
              )}
            </div>
          </div>
          <div className="tw-flex tw-justify-between tw-items-center">
            <div>
              {getLanguageVarValue("LANG_neobooking_fci_recap_total_paid")}
            </div>
            <div className="tw-font-bold">
              {getTotalPaid(cart) <= 0 ? "" : "-"}
              {getTotalPaid(cart).toFixed(2)}
              {getCurrencySymbol(
                cart?.reservations?.[0]?.totalAmount?.currencyCode
              )}
            </div>
          </div>
          {showRemaining ? (
            <div className="tw-flex tw-justify-between tw-items-center">
              <div>
                {getLanguageVarValue(
                  "LANG_neobooking_fci_recap_total_remaining"
                )}
              </div>
              <div className="tw-font-bold tw-text-v_red-dark">
                {getTotalRemaining(cart)}
                {getCurrencySymbol(
                  cart?.reservations?.[0]?.totalAmount?.currencyCode
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
    </React.Fragment>
  );
};

type DistributionBaseProps = {
  cart: Cart | null;
};
const DistributionBase: React.FC<DistributionBaseProps> = ({ cart }) => {
  return (
    <React.Fragment>
      {cart?.reservations?.[0]?.stays?.map((s, si) => {
        return s?.distribution?.roomTypes?.map((r, i) => {
          return (
            <div
              key={`stay_${s.guiid}_${si}_room_${r?.label}_${i}`}
              className="tw-flex tw-justify-between tw-items-center tw-gap-2"
            >
              <div>
                <span className="tw-font-bold">{r?.quantity} x</span>{" "}
                <span className="tw-text-lg">{r?.label}</span>
              </div>
              <div className="tw-font-bold">
                {getBaseLineItem(
                  cart,
                  s?.guiid!!
                )?.totalAmount?.amount?.toFixed(2)}
                {getCurrencySymbol(
                  getBaseLineItem(cart, s?.guiid!!)?.totalAmount?.currencyCode!!
                )}
              </div>
            </div>
          );
        });
      })}
    </React.Fragment>
  );
};

export default FCIPage;
